/** Shared low-level buffer helpers for spreadsheet ingestion. Used by both the
 *  read-only DocumentViewer and the editable SheetEditor so the detection logic
 *  stays in one place. */

/** Returns true if the buffer starts with the ZIP magic bytes (PK\x03\x04),
 *  meaning the file is actually XLSX regardless of the declared MIME type. */
export const isXlsxBuffer = (buffer: ArrayBuffer): boolean => {
    if (buffer.byteLength < 4) return false;
    const b = new Uint8Array(buffer);
    return b[0] === 0x50 && b[1] === 0x4b && b[2] === 0x03 && b[3] === 0x04;
};

/** Decodes an ArrayBuffer as text, respecting UTF-16 LE/BE and UTF-8 BOMs. */
export const decodeText = (buffer: ArrayBuffer): string => {
    const b = new Uint8Array(buffer);
    if (b[0] === 0xff && b[1] === 0xfe) return new TextDecoder('utf-16le').decode(buffer.slice(2));
    if (b[0] === 0xfe && b[1] === 0xff) return new TextDecoder('utf-16be').decode(buffer.slice(2));
    if (b[0] === 0xef && b[1] === 0xbb && b[2] === 0xbf) return new TextDecoder('utf-8').decode(buffer.slice(3));
    return new TextDecoder('utf-8').decode(buffer);
};

/** OLE2/CFB compound-file magic bytes — the binary container format for both
 *  legacy .doc and legacy .xls (BIFF5/BIFF8). Shared with DocxViewer.tsx so
 *  the constant lives in one place. */
export const OLE2_MAGIC = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

/** Returns true if the buffer starts with OLE2/CFB magic bytes, meaning the
 *  file is a legacy binary BIFF5/BIFF8 .xls regardless of the declared
 *  extension or MIME type — content beats a (possibly stale or mislabelled)
 *  filename, same rationale as isXlsxBuffer above. */
export const isLegacyXlsBuffer = (buffer: ArrayBuffer): boolean => {
    if (buffer.byteLength < OLE2_MAGIC.length) return false;
    const b = new Uint8Array(buffer);
    return OLE2_MAGIC.every((byte, i) => b[i] === byte);
};

// Bounds how much of a buffer leadsToMarkup decodes — a sniff of the first
// non-whitespace character must never decode a whole multi-MB binary.
const MARKUP_SNIFF_BYTES = 1024;

/** Returns true if the buffer's content, after skipping any leading run of
 *  '\r'/'\n'/' '/'\t', leads with '<' — the HTML-table and SpreadsheetML-2003
 *  .xls variants carry no stable magic bytes, so this is the second half of
 *  the legacy-.xls dispatch gate (see converters.ts fileBytesToFortuneSheets).
 *  Close cousin of sheetSizeGuard.ts's private leadsToXmlOrHtml (not imported,
 *  since that predicate is module-private and the repo's import convention
 *  runs one way: fileTypes.ts keeps converters.ts's heavier import graph OUT
 *  of sheetSizeGuard.ts, so reversing that arrow for one caller is the thing
 *  to avoid) — this gate also skips a leading tab, which that one doesn't.
 *  Decodes only a bounded prefix — decodeText already handles the UTF-16/
 *  UTF-8 BOMs a SpreadsheetML file may carry, and a prefix slice keeps the
 *  BOM at offset 0; it can only mangle a trailing multi-byte char, which a
 *  lead-only sniff never reads. */
export const leadsToMarkup = (buffer: ArrayBuffer): boolean => {
    const text = decodeText(buffer.byteLength > MARKUP_SNIFF_BYTES ? buffer.slice(0, MARKUP_SNIFF_BYTES) : buffer);
    let i = 0;
    while (i < text.length && (text[i] === '\r' || text[i] === '\n' || text[i] === ' ' || text[i] === '\t')) i += 1;
    return text[i] === '<';
};
